import React from 'react';
import { cn } from '../../lib/utils';
import { Star, Phone, Mail, MessageCircle } from 'lucide-react';
import { Button } from '../ui';

interface TechnicianProfileProps {
  photo?: string;
  name: string;
  rating?: number;
  reviewCount?: number;
  specializations?: string[];
  phone?: string;
  email?: string;
  showContactButtons?: boolean;
  size?: 'small' | 'medium' | 'large';
  variant?: 'card' | 'inline';
}

export function TechnicianProfile({
  photo,
  name,
  rating,
  reviewCount,
  specializations,
  phone,
  email,
  showContactButtons = false,
  size = 'medium',
  variant = 'inline',
}: TechnicianProfileProps) {
  const sizeClasses = {
    small: 'h-10 w-10',
    medium: 'h-16 w-16',
    large: 'h-24 w-24',
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  if (variant === 'card') {
    return (
      <div className="rounded-xl border bg-card p-6">
        <div className="flex items-start gap-4">
          {/* Avatar */}
          <div className={cn(
            "rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0 overflow-hidden border-2 border-primary/20",
            sizeClasses[size]
          )}>
            {photo ? (
              <img src={photo} alt={name} className="h-full w-full object-cover" />
            ) : (
              <span className={cn(
                "font-semibold text-primary",
                size === 'small' && "text-xs",
                size === 'medium' && "text-lg",
                size === 'large' && "text-2xl"
              )}>
                {getInitials(name)}
              </span>
            )}
          </div>

          {/* Info */}
          <div className="flex-1 min-w-0">
            <h3 className={cn(
              "font-semibold truncate",
              size === 'small' && "text-sm",
              size === 'medium' && "text-lg",
              size === 'large' && "text-xl"
            )}>
              {name}
            </h3>

            {/* Rating */}
            {rating && (
              <div className="flex items-center gap-2 mt-1">
                <div className="flex items-center gap-1">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span className="font-semibold text-sm">{rating.toFixed(1)}</span>
                </div>
                {reviewCount && (
                  <span className="text-xs text-muted-foreground">
                    ({reviewCount} reviews)
                  </span>
                )}
              </div>
            )}

            {/* Specializations */}
            {specializations && specializations.length > 0 && (
              <div className="flex gap-1.5 mt-2 flex-wrap">
                {specializations.map((spec, i) => (
                  <span
                    key={i}
                    className="px-2 py-0.5 rounded-md bg-secondary text-xs font-medium"
                  >
                    {spec}
                  </span>
                ))}
              </div>
            )}

            {/* Contact Info */}
            {(phone || email) && (
              <div className="mt-3 space-y-1.5">
                {phone && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Phone className="h-3.5 w-3.5" />
                    <span>{phone}</span>
                  </div>
                )}
                {email && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Mail className="h-3.5 w-3.5" />
                    <span className="truncate">{email}</span>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            {showContactButtons && (phone || email) && (
              <div className="flex gap-2 mt-4">
                {phone && (
                  <Button size="sm" variant="outline" className="flex-1">
                    <Phone className="h-3.5 w-3.5 mr-1.5" />
                    Call
                  </Button>
                )}
                <Button size="sm" variant="outline" className="flex-1">
                  <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                  Message
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Inline variant
  return (
    <div className="flex items-center gap-3">
      {/* Avatar */}
      <div className={cn(
        "rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center flex-shrink-0 overflow-hidden border border-primary/20",
        sizeClasses[size]
      )}>
        {photo ? (
          <img src={photo} alt={name} className="h-full w-full object-cover" />
        ) : (
          <span className={cn(
            "font-semibold text-primary",
            size === 'small' && "text-xs",
            size === 'medium' && "text-base",
            size === 'large' && "text-xl"
          )}>
            {getInitials(name)}
          </span>
        )}
      </div>

      {/* Info */}
      <div className="flex-1 min-w-0">
        <div className={cn(
          "font-semibold truncate",
          size === 'small' && "text-sm",
          size === 'medium' && "text-base",
          size === 'large' && "text-lg"
        )}>
          {name}
        </div>

        {/* Rating */}
        {rating && (
          <div className="flex items-center gap-1.5 mt-0.5">
            <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />
            <span className="text-sm font-medium">{rating.toFixed(1)}</span>
            {reviewCount && (
              <span className="text-xs text-muted-foreground">
                ({reviewCount})
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
